import clsx from 'clsx'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import screenfull from 'screenfull'

import { baseUrl, previewVideo } from '~/configs/app'
import usePreview from '~/hooks/usePreview'
import { useWindowDimensions } from '~/hooks/useWindowDimensions'
import type { PreviewScreenProps } from '~/types/preview'

import Button from '../common/Button'
import SelectLocationForm from './SelectLocationForm'

const PreviewScreen = ({ sound = false }: PreviewScreenProps) => {
  const [videoType, setVideoType] = useState('')
  const [videoQueue, setVideoQueue] = useState<File[]>([])
  const [videoStatus, setVideoStatus] = useState(false)
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
  const { isPreviewStarted, queueList, calledTicket, locationName } = usePreview()
  const { height: windowHeight, width: windowWidth } = useWindowDimensions()

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleVideoFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (selectedFiles && selectedFiles.length > 0) {
      const areAllValidMP4 = Array.from(selectedFiles).every((file) => {
        return /\.(mp4)$/i.test(file.name) && file.type === 'video/mp4'
      })

      if (areAllValidMP4) {
        setVideoQueue([...videoQueue, ...selectedFiles])
      } else {
        toast.error('Please select valid video files')
      }
    }
  }

  const playNextVideo = () => {
    if (currentVideoIndex < videoQueue.length) {
      const nextVideo = videoQueue[currentVideoIndex]
      const videoURL = URL.createObjectURL(nextVideo)
      if (videoRef.current) {
        videoRef.current.src = videoURL
        videoRef.current.load()
      }
      setCurrentVideoIndex(currentVideoIndex + 1)
    } else {
      setCurrentVideoIndex(0)
    }
  }

  const handleSelectOfflineVideo = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handlePlayOfflineVideo = () => {
    setVideoType('offline')
    screenfull
      .request()
      .then(() => toast.success('fullscreen enabled'))
      .catch(() => toast.error('fullscreen not allowed'))
    setVideoStatus(true)
  }

  useEffect(() => {
    if (videoStatus) {
      if (currentVideoIndex !== 0) {
        if (videoRef.current) {
          videoRef.current.onended = playNextVideo
        }
      } else {
        playNextVideo()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVideoIndex, videoStatus])

  if (!isPreviewStarted) return <SelectLocationForm sound={sound} />

  return (
    <div className="flex h-full w-full flex-col justify-between gap-[4vh]">
      <div
        className={clsx(
          'flex flex-1 gap-[4vw]',
          [windowHeight < 500 && windowWidth < 1200 && 'h-[calc(100vh-260px)]'],
          [windowHeight < 764 && windowWidth < 1200 && 'h-[calc(100vh-300px)]'],
          [windowHeight < 500 && windowWidth > 1200 && 'h-[calc(100vh-200px)]'],
          [windowHeight < 764 && windowWidth > 1200 && 'h-[calc(100vh-400px)]'],
          [windowHeight > 764 && windowWidth > 1200 && 'h-[calc(100vh-440px)]'],
        )}
      >
        <div className="flex w-[33%] shrink-0 flex-col gap-[4vh]">
          <div className="flex items-center gap-[2vw]">
            <img src="/jba-logo.svg" className="h-[4vw]" />
            <div className="text-[4vh] font-semibold text-jba-blue">JBA {locationName}</div>
          </div>
          <div
            className={clsx('flex h-full items-center justify-center rounded-2xl text-center', [
              calledTicket.queue_no
                ? 'bg-gradient-to-b from-jba-blue via-jba-blueTicket to-jba-darkPurple'
                : 'bg-gray-300',
            ])}
          >
            {calledTicket.queue_no ? (
              <div className="text-center text-white">
                <div className="text-[3vw] font-bold">Loket {calledTicket.counter_lane}</div>
                <div className="text-[1.5vw]">Antrean Nomor</div>
                <div className="text-[4vw] font-bold">{calledTicket.queue_no}</div>
              </div>
            ) : (
              <div className="text-[1.5vw] text-gray-500">Belum ada antrean yang dipanggil</div>
            )}
          </div>
        </div>
        <div className={clsx('h-full w-full rounded-xl', [sound || !videoType ? 'bg-white' : 'bg-black'])}>
          {!sound && (
            <>
              {videoType ? (
                <>
                  {videoType === 'online' ? (
                    <video
                      src={`${baseUrl}${previewVideo}`}
                      loop
                      autoPlay
                      controls
                      className="h-full w-full rounded-xl object-contain object-center"
                    />
                  ) : (
                    <video
                      ref={videoRef}
                      autoPlay
                      controls
                      className="h-full w-full rounded-xl object-contain object-center"
                    />
                  )}
                </>
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4"
                    onChange={handleVideoFile}
                    multiple
                    className="hidden"
                  />
                  <div className="grid grid-cols-2 items-center gap-6">
                    {videoQueue.length > 0 ? (
                      <>
                        <ul className="max-h-60 max-w-xs overflow-auto rounded-xl bg-gray-50 p-4">
                          {videoQueue.map((file) => (
                            <li key={file.name} className="ml-6 list-decimal">
                              {file.name}
                            </li>
                          ))}
                        </ul>
                        <Button type="button" variant="primary" onClick={handlePlayOfflineVideo}>
                          Play Video
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button type="button" variant="primary" onClick={handleSelectOfflineVideo}>
                          Play Offline Video
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => setVideoType('online')}>
                          Play Online Video
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div>
        <div>
          {Object.keys(queueList).length ? (
            <>
              <div className="mb-[2vh] text-[4vh] font-semibold text-jba-blue">Dalam Pelayanan</div>
              <div className="flex gap-6">
                {Object.keys(queueList).map((ticket) => (
                  <div
                    key={ticket}
                    className={clsx(
                      'relative w-full max-w-xs rounded-xl bg-jba-blueTicket text-center font-bold text-white',
                    )}
                  >
                    <div className="p-4 text-[3.5vh]">Loket {ticket}</div>
                    <div
                      className={clsx(
                        'relative border-t-2 border-dashed border-[#e8e9ef]',
                        'before:absolute before:inset-y-0 before:left-0 before:z-10 before:my-auto before:inline-block before:h-8 before:w-4 before:rounded-r-full before:bg-[#e8e9ef]',
                        'after:absolute after:inset-y-0 after:right-0 after:z-10 after:my-auto after:inline-block after:h-8 after:w-4 after:rounded-l-full after:bg-[#e8e9ef]',
                      )}
                    />
                    <div className="px-[1vh] py-[2vh] text-[5vh]">{queueList[ticket]}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center rounded-2xl bg-gray-300 p-16 text-[2vw] text-gray-500">
              Belum ada antrean yang dilayani
            </div>
          )}
        </div>
        <div className="pt-[1vw] text-center text-[0.75vw] text-gray-500">
          Copyright © {new Date().getFullYear()} JBA Antrean System. All rights reserved.
        </div>
      </div>
    </div>
  )
}

export default PreviewScreen
